import { MANTRA_MODULES } from './mantraModules.js';
import { canonicalMantraRoles } from './studioCalc.js';

/**
 * Gerarchie ruolo Mantra globali — export simulatore 2026-08-29.
 * docs/superpowers/specs/rosa-role-hierarchy-simulator.html
 */
export const MANTRA_ROLE_DEPARTMENTS = {
  gk: { label: 'Portieri', order: ['P'] },
  def: { label: 'Difesa', order: ['E', 'DS', 'DD', 'B', 'DC'] },
  mid: { label: 'Centrocampo', order: ['T', 'M', 'C'] },
  att: { label: 'Attacco', order: ['W', 'A', 'PC', 'T'] },
};

/** 1 = preferito in asta / risoluzione slot. */
export const MANTRA_GLOBAL_ROLE_RANK = {
  W: 1,
  A: 2,
  PC: 3,
  T: 4,
  M: 5,
  C: 6,
  E: 7,
  DS: 8,
  DD: 9,
  B: 10,
  DC: 11,
  P: 12,
};

/** Slash → ruolo con cui schierare (es. giocatore M/C gioca come M). */
export const MANTRA_SLASH_RULES = {
  'A/PC': 'A',
  'A/PC/T': 'T',
  'A/T': 'T',
  'A/W': 'W',
  'B/DC': 'B',
  'C/M': 'M',
  'C/T': 'C',
  'E/W': 'E',
  'T/W': 'T',
};

function normalizeMovementRoles(roles) {
  return (roles || [])
    .map((r) => String(r).trim().toUpperCase())
    .filter((r) => r && r !== 'P');
}

/** Chiave slash ordinata (es. ['W','A'] → 'A/W'). */
export function slotRoleKey(roles) {
  return normalizeMovementRoles(roles).sort().join('/');
}

function pickByGlobalRank(roles) {
  return roles
    .slice()
    .sort((a, b) => (MANTRA_GLOBAL_ROLE_RANK[a] ?? 99) - (MANTRA_GLOBAL_ROLE_RANK[b] ?? 99))[0];
}

/**
 * Ruolo con cui schierare un giocatore negli slot accettati (intersezione ruoli giocatore ∩ slot).
 */
export function resolveDeploymentRole(playerRoles, acceptedRoles) {
  const acceptedCanon = canonicalMantraRoles(acceptedRoles);
  const playerCanon = canonicalMantraRoles(playerRoles);
  if (acceptedCanon.includes('P') && playerCanon.includes('P')) return 'P';

  const accepted = normalizeMovementRoles(acceptedCanon);
  const matching = normalizeMovementRoles(playerCanon).filter((r) => accepted.includes(r));
  if (matching.length === 0) return null;
  return resolvePreferredSlotRole(matching);
}
/**
 * Ruolo preferito per uno slot modulo (titolare logico).
 * @returns {string|null}
 */
export function resolvePreferredSlotRole(roles) {
  const canon = canonicalMantraRoles(roles);
  if (canon.length === 1 && canon[0] === 'P') return 'P';

  const movement = normalizeMovementRoles(canon);
  if (movement.length === 0) return null;
  if (movement.length === 1) return movement[0];
  const key = slotRoleKey(roles);
  const rule = MANTRA_SLASH_RULES[key];
  if (rule && movement.includes(rule)) return rule;
  return pickByGlobalRank(movement);
}

/**
 * @returns {{ pick: string|null, source: 'single'|'manual'|'auto'|null }}
 */
export function resolveSlotRoleWithSource(roles) {
  const movement = normalizeMovementRoles(roles);
  if (movement.length === 0) return { pick: null, source: null };
  if (movement.length === 1) return { pick: movement[0], source: 'single' };
  const key = slotRoleKey(roles);
  const rule = MANTRA_SLASH_RULES[key];
  if (rule && movement.includes(rule)) {
    return { pick: rule, source: 'manual' };
  }
  return { pick: pickByGlobalRank(movement), source: 'auto' };
}

/** Anteprima slot → ruolo preferito per un modulo. */
export function resolveModuleSlotPicks(moduleId, modules = MANTRA_MODULES) {
  const slots = modules[moduleId];
  if (!slots) return [];
  return slots.map((slot, slotIndex) => {
    const { pick, source } = resolveSlotRoleWithSource(slot);
    return {
      slotIndex,
      slot: slot.join('/'),
      pick,
      source,
    };
  });
}

export function buildGlobalRoleRankFromDepartments(departments = MANTRA_ROLE_DEPARTMENTS) {
  const rank = {};
  let n = 1;
  for (const key of ['att', 'mid', 'def', 'gk']) {
    const order = departments[key]?.order || [];
    for (const role of order) {
      if (!(role in rank)) rank[role] = n++;
    }
  }
  return rank;
}
